import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Archive, Loader2, Download, ArrowRight } from "lucide-react";

const STAY = "__stay__";

function ArchiveList({ archives }) {
  const { t } = useLang();
  const download = async (a) => {
    const res = await api.get(`/archives/${a.id}/excel`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const el = document.createElement("a");
    el.href = url; el.download = `archive-${a.label}.xlsx`; el.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="surface-card rounded-xl border border-border bg-card p-5 space-y-3" data-testid="archives-list">
      <h2 className="font-heading font-semibold text-foreground">{t("newYear.archives")}</h2>
      {archives.length === 0 && <p className="text-sm text-muted-foreground">{t("newYear.noArchives")}</p>}
      {archives.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-4 py-3 text-sm" data-testid={`archive-${a.id}`}>
          <div>
            <div className="font-medium text-foreground">{a.label}</div>
            <div className="text-xs text-muted-foreground">{a.records} {t("newYear.records")} · {new Date(a.created_at).toLocaleDateString()}</div>
          </div>
          <button onClick={() => download(a)} data-testid={`archive-download-${a.id}`} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 font-medium hover:bg-secondary">
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>
      ))}
    </div>
  );
}

export default function NewYear() {
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [archives, setArchives] = useState([]);
  const [label, setLabel] = useState("");
  const [map, setMap] = useState({});
  const [clear, setClear] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [g, a] = await Promise.all([api.get("/groups"), api.get("/archives")]);
    setGroups(g.data);
    setArchives(a.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(t("newYear.confirm"))) return;
    setBusy(true);
    try {
      const promotions = Object.entries(map).filter(([, to]) => to && to !== STAY).map(([from, to]) => ({ from_group_id: from, to_group_id: to }));
      const { data } = await api.post("/new-year", { label, promotions, clear_attendance: clear });
      toast.success(`${t("newYear.done")}: ${data.archived} / ${data.moved} / ${data.cleared}`);
      setLabel(""); setMap({}); setClear(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-year-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("newYear.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("newYear.subtitle")}</p>
      </div>
      <form onSubmit={submit} className="surface-card rounded-xl border border-border bg-card p-5 space-y-5">
        <div className="space-y-2 max-w-sm">
          <Label>{t("newYear.label")}</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="2025-2026" data-testid="new-year-label" required />
        </div>
        <div className="space-y-3">
          <Label>{t("newYear.promote")}</Label>
          {groups.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`promote-row-${g.id}`}>
              <span className="w-40 font-medium text-foreground">{g.name}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground rtl:rotate-180" />
              <Select value={map[g.id] || STAY} onValueChange={(v) => setMap((m) => ({ ...m, [g.id]: v }))}>
                <SelectTrigger className="w-52" data-testid={`promote-select-${g.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={STAY}>{t("newYear.stay")}</SelectItem>
                  {groups.filter((o) => o.id !== g.id).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox checked={clear} onCheckedChange={(v) => setClear(!!v)} data-testid="new-year-clear" />
          <span><span className="font-medium text-foreground">{t("newYear.clear")}</span><br /><span className="text-muted-foreground">{t("newYear.clearHelp")}</span></span>
        </label>
        <button type="submit" disabled={busy} data-testid="new-year-submit" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} {t("newYear.start")}
        </button>
      </form>
      <ArchiveList archives={archives} />
    </div>
  );
}
